import { toastError } from "helpers";
import { createLogger } from "logging";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { ToggleType } from "models/ToggleType";
import { WorkingDocumentModel } from "models/WorkingDocumentModel";
import { useApi, useMeeting, useTranslations } from "providers";
import React, { PropsWithChildren, startTransition, useCallback, useEffect, useMemo, useState } from "react";

import { ConfirmDeleteDocument } from "./components/ConfirmDeleteDocument";
import { UploadDocument } from "./components/UploadDocument";
import { DocumentContext } from "./DocumentContext";

const logger = createLogger("DocumentProvider");

export const DocumentProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const { meeting, reloadMeeting } = useMeeting();
  const { workingDocumentsApi } = useApi();

  const [deleting, setDeleting] = useState<ToggleType>({});
  const [updating, setUpdating] = useState<ToggleType>({});
  const [confirmDeleteDocument, setConfirmDeleteDocument] = useState<ConfirmDeleteTarget | undefined>(undefined);
  const [selectDocumentId, setSelectDocumentId] = useState<number>(-1);
  const [editDocumentId, setEditDocumentId] = useState<number>(-1);
  const [uploadFileAgendaItemId, setUploadFileAgendaItemId] = useState<number>(-1);
  const [uploadingAgendaItemId, setUploadingAgendaItemId] = useState<number>(-1);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [documents, setDocuments] = useState<WorkingDocumentModel[]>();

  const handleSetDocuments = useCallback(
    (itemsToSet: WorkingDocumentModel[]) => {
      setDocuments(itemsToSet);
      const newSelectedId = itemsToSet.find((x) => x.id === selectDocumentId)
        ? selectDocumentId
        : itemsToSet.length > 0
          ? itemsToSet[0].id
          : -1;
      setSelectDocumentId(newSelectedId);
    },
    [selectDocumentId]
  );

  useEffect(() => {
    startTransition(() => {
      handleSetDocuments(meeting?.workingDocuments ?? []);
    });
  }, [meeting, handleSetDocuments]);

  const isDocumentUpdating = useCallback(
    (id: number): boolean => {
      const flag = updating[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [updating]
  );

  const isDocumentDeleting = useCallback(
    (id: number): boolean => {
      const flag = deleting[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [deleting]
  );

  const setDocumentUpdating = useCallback((id: number, newState: boolean) => {
    setUpdating((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const setDocumentDeleting = useCallback((id: number, newState: boolean) => {
    setDeleting((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const onFileChanged = useCallback(
    (file: File) => {
      if (uploadFileAgendaItemId <= 0) {
        return;
      }

      const targetAgendaItemId = uploadFileAgendaItemId;
      const formData = new FormData();
      formData.append("file", file);

      setUploadingAgendaItemId(targetAgendaItemId);
      setIsUploading(true);

      workingDocumentsApi
        .uploadAgendaItemFile(targetAgendaItemId, formData)
        .then((data) => {
          logger.info("uploaded", data);
          reloadMeeting();
        })
        .catch((error) => {
          toastError(i18n.Upload_StatusUploadFailed, error);
        })
        .finally(() => {
          setIsUploading(false);
          setUploadingAgendaItemId(-1);
          setUploadFileAgendaItemId(0);
        });
    },
    [i18n, reloadMeeting, uploadFileAgendaItemId, workingDocumentsApi]
  );

  const contextValue = useMemo(
    () => ({
      documents,
      setDocuments,
      uploadFileAgendaItemId,
      setUploadFileAgendaItemId,
      uploadingAgendaItemId,
      isUploading,
      onFileChanged,
      confirmDeleteDocument,
      setConfirmDeleteDocument,
      selectDocumentId,
      setSelectDocumentId,
      editDocumentId,
      setEditDocumentId,
      isDocumentUpdating,
      setDocumentUpdating,
      isDocumentDeleting,
      setDocumentDeleting,
    }),
    [
      documents,
      setDocuments,
      uploadFileAgendaItemId,
      setUploadFileAgendaItemId,
      uploadingAgendaItemId,
      isUploading,
      onFileChanged,
      confirmDeleteDocument,
      setConfirmDeleteDocument,
      selectDocumentId,
      setSelectDocumentId,
      editDocumentId,
      setEditDocumentId,
      isDocumentUpdating,
      setDocumentUpdating,
      isDocumentDeleting,
      setDocumentDeleting,
    ]
  );

  return (
    <DocumentContext.Provider value={contextValue}>
      {children}
      <UploadDocument
        uploadFileAgendaItemId={uploadFileAgendaItemId}
        setUploadFileAgendaItemId={setUploadFileAgendaItemId}
        onFileChanged={onFileChanged}
      />

      <ConfirmDeleteDocument />
    </DocumentContext.Provider>
  );
};
